import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TaxRateService } from "./taxRate.service";

const initialState = {
  ready: true, // false until the tax migration has run on the server
  rates: [],
  groups: [],
  groupItems: [],
  productOverrides: [],
  businessRate: 0,
  loaded: false,
  isLoading: false,
  isSaving: false,
  error: null,
};

const service = new TaxRateService();
const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const fetchTaxRates = createAsyncThunk(
  "taxRate/fetchTaxRates",
  async (_, { rejectWithValue }) => {
    try {
      return await service.getTaxRates();
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to load tax rates"));
    }
  }
);

// Writes reject with a plain message so pages can `unwrap()` and toast it.
const writeThunk = (type, call, fallback) =>
  createAsyncThunk(`taxRate/${type}`, async (arg, { rejectWithValue, dispatch }) => {
    try {
      const response = await call(arg);
      dispatch(fetchTaxRates());
      return response;
    } catch (error) {
      return rejectWithValue(errorMessage(error, fallback));
    }
  });

export const addTaxRate = writeThunk("addTaxRate", (d) => service.addTaxRate(d), "Failed to add tax rate");
export const addTaxGroup = writeThunk("addTaxGroup", (d) => service.addTaxGroup(d), "Failed to add tax group");
export const updateTax = writeThunk("updateTax", (d) => service.updateTax(d), "Failed to update tax");
export const deleteTax = writeThunk("deleteTax", (id) => service.deleteTax(id), "Failed to delete tax");

const writes = [addTaxRate, addTaxGroup, updateTax, deleteTax];

export const taxRateSlice = createSlice({
  name: "taxRate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaxRates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTaxRates.fulfilled, (state, action) => {
        const d = action.payload?.data || {};
        state.isLoading = false;
        state.loaded = true;
        state.error = null;
        state.ready = d.ready !== false;
        state.rates = d.rates || [];
        state.groups = d.groups || [];
        state.groupItems = d.group_items || [];
        state.productOverrides = d.product_overrides || [];
        state.businessRate = Number(d.business_rate) || 0;
      })
      .addCase(fetchTaxRates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    writes.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.isSaving = true;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.isSaving = false;
        })
        .addCase(thunk.rejected, (state) => {
          state.isSaving = false;
        });
    });
  },
});

export default taxRateSlice.reducer;
